import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Mail, Globe, Building2, Star, ExternalLink, Heart, Share2, Send, Edit2, Trash2, User, X } from 'lucide-react';
import { Map, AdvancedMarker, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { getPlaceById, linkPlaceToPeople, unlinkPlaceFromPeople, getPlaceRelation } from '../../services/placesService';
import { getPlaceReviews, getPlaceStats, createReview, updateReview, deleteReview } from '../../services/placesAvisService';
import { getFileUrl } from '../../services/filesService';
import Button from '../ui/Button';

// Google Maps icon
const GoogleMapsIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EA4335"/>
    </svg>
);

// Social share icons
const FacebookIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const WhatsAppIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

/**
 * Star Rating Component
 */
const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
    const [hoverRating, setHoverRating] = useState(0);
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6'
    };

    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    disabled={!interactive}
                    onClick={() => interactive && onRate?.(star)}
                    onMouseEnter={() => interactive && setHoverRating(star)}
                    onMouseLeave={() => interactive && setHoverRating(0)}
                    className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
                >
                    <Star
                        className={`${sizeClasses[size]} ${
                            (hoverRating || rating) >= star
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                        }`}
                    />
                </button>
            ))}
        </div>
    );
};

/**
 * Rating Distribution Bar
 */
const RatingBar = ({ stars, count, total }) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className="w-3 text-gray-600">{stars}</span>
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                    className="h-full bg-yellow-400 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <span className="w-8 text-gray-500 text-right">{count}</span>
        </div>
    );
};

/**
 * Review Card Component
 */
const ReviewCard = ({ review, isOwn, onEdit, onDelete }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <div className={`p-4 rounded-lg border ${isOwn ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    {review.peopleImgUrl ? (
                        <img
                            src={getFileUrl(review.peopleImgUrl)}
                            alt={`${review.firstName} ${review.lastName}`}
                            className="w-10 h-10 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                        </div>
                    )}
                    <div>
                        <p className="font-medium text-gray-900">
                            {review.firstName} {review.lastName}
                            {isOwn && <span className="ml-2 text-xs text-purple-600">(vous)</span>}
                        </p>
                        <div className="flex items-center gap-2">
                            <StarRating rating={review.rating} size="sm" />
                            <span className="text-xs text-gray-500">{formatDate(review.createdAt)}</span>
                        </div>
                    </div>
                </div>
                {isOwn && (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => onEdit(review)}
                            className="p-1.5 text-gray-500 hover:text-cabaza-purple hover:bg-purple-50 rounded transition-colors"
                            title="Modifier"
                        >
                            <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => onDelete(review.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Supprimer"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>
            {review.comment && (
                <p className="mt-3 text-gray-700 text-sm">{review.comment}</p>
            )}
        </div>
    );
};

/**
 * Endroit Detail Content Component
 */
const EndroitDetailContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const apiIsLoaded = useApiIsLoaded();
    const [place, setPlace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Reviews state
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState({ totalReviews: 0, averageRating: 0, ratingDistribution: {} });
    const [loadingReviews, setLoadingReviews] = useState(false);

    // New review form
    const [newRating, setNewRating] = useState(0);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [editingReview, setEditingReview] = useState(null);

    // Favorite state
    const [isFavorite, setIsFavorite] = useState(false);
    const [toggleFavoriteLoading, setToggleFavoriteLoading] = useState(false);

    // Google Maps modal
    const [showGoogleMapsModal, setShowGoogleMapsModal] = useState(false);

    const userId = parseInt(localStorage.getItem('userId') || '0', 10);

    useEffect(() => {
        loadPlace();
    }, [id]);

    const loadPlace = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getPlaceById(id);
            if (!data) {
                setError('Endroit non trouvé');
                return;
            }
            setPlace(data);

            // Load reviews and stats
            loadReviews();
            loadStats();

            // Check if favorite
            if (userId > 0) {
                checkFavoriteStatus();
            }
        } catch (err) {
            console.error('Error loading place:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadReviews = async () => {
        setLoadingReviews(true);
        try {
            const data = await getPlaceReviews(id);
            setReviews(data || []);
        } catch (err) {
            console.error('Error loading reviews:', err);
        } finally {
            setLoadingReviews(false);
        }
    };

    const loadStats = async () => {
        try {
            const data = await getPlaceStats(id);
            setStats(data);
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    };

    const checkFavoriteStatus = async () => {
        try {
            const result = await getPlaceRelation(id, userId);
            if (result.success && result.data) {
                setIsFavorite(result.data.isFavorite || result.data.isfavorite || false);
            }
        } catch (err) {
            console.error('Error checking favorite status:', err);
        }
    };

    const handleToggleFavorite = async () => {
        if (userId <= 0) return;
        setToggleFavoriteLoading(true);
        try {
            if (isFavorite) {
                await unlinkPlaceFromPeople(userId, parseInt(id));
            } else {
                await linkPlaceToPeople(userId, parseInt(id), 'visitor', true);
            }
            setIsFavorite(!isFavorite);
        } catch (err) {
            console.error('Error toggling favorite:', err);
        } finally {
            setToggleFavoriteLoading(false);
        }
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        if (newRating === 0) return;

        setSubmitting(true);
        try {
            if (editingReview) {
                // Update existing review
                const result = await updateReview(editingReview.id, {
                    rating: newRating,
                    comment: newComment
                });
                if (result.success) {
                    setEditingReview(null);
                    setNewRating(0);
                    setNewComment('');
                    loadReviews();
                    loadStats();
                }
            } else {
                // Create new review
                const result = await createReview(parseInt(id), newRating, newComment);
                if (result.success) {
                    setNewRating(0);
                    setNewComment('');
                    loadReviews();
                    loadStats();
                } else if (result.duplicate) {
                    alert('Vous avez déjà donné un avis pour cet endroit. Vous pouvez le modifier ci-dessous.');
                } else if (result.message) {
                    alert(result.message);
                }
            }
        } catch (err) {
            console.error('Error submitting review:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditReview = (review) => {
        setEditingReview(review);
        setNewRating(review.rating);
        setNewComment(review.comment || '');
        // Scroll to form
        document.getElementById('review-form')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleDeleteReview = async (reviewId) => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer votre avis ?')) return;

        try {
            const result = await deleteReview(reviewId);
            if (result.success) {
                loadReviews();
                loadStats();
            }
        } catch (err) {
            console.error('Error deleting review:', err);
        }
    };

    const handleCancelEdit = () => {
        setEditingReview(null);
        setNewRating(0);
        setNewComment('');
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/endroits');
        }
    };

    // Check if user already has a review
    const userReview = reviews.find(r => r.peopleId === userId);

    if (loading) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-cabaza-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 text-lg">Chargement...</p>
            </div>
        );
    }

    if (error || !place) {
        return (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center max-w-md mx-auto">
                <p className="text-red-800 mb-4">{error || 'Endroit non trouvé'}</p>
                <button
                    onClick={handleBack}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                    Retour
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white print:shadow-none">
                {/* Document metadata */}
                <title>{place.placeName} - Cabaza</title>
                <meta name="description" content={`${place.placeName}${place.city ? ` - ${place.city}` : ''}`} />

                {/* Back button - Hidden on print */}
                <div className="mb-6 print:hidden">
                    <button
                        onClick={handleBack}
                        className="flex items-center text-cabaza-purple hover:text-cabaza-pink transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Retour
                    </button>
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 print:shadow-none print:border-0">
                    {/* Header */}
                    <div className="p-6 sm:p-8 border-b border-gray-100">
                        <div className="flex flex-col sm:flex-row items-start gap-6">
                            {/* Place Icon/Image */}
                            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-cabaza-purple to-cabaza-pink rounded-xl flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                                <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                            </div>

                            <div className="flex-1 min-w-0 text-center sm:text-left">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                    {place.placeName}
                                </h1>

                                {/* Rating summary */}
                                {stats.totalReviews > 0 && (
                                    <div className="flex items-center gap-2 justify-center sm:justify-start mb-3">
                                        <StarRating rating={Math.round(stats.averageRating)} />
                                        <span className="font-semibold text-gray-900">{stats.averageRating.toFixed(1)}</span>
                                        <span className="text-gray-500">({stats.totalReviews} avis)</span>
                                    </div>
                                )}

                                {/* Category/Type badges */}
                                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                                    {place.placeType && (
                                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                            {place.placeType}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Actions - Hidden on print */}
                            <div className="flex flex-col gap-3 print:hidden">
                                {/* Favorite button */}
                                <button
                                    onClick={handleToggleFavorite}
                                    disabled={toggleFavoriteLoading || userId <= 0}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                        isFavorite
                                            ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                                    }`}
                                >
                                    <Heart className={`w-4 h-4 ${isFavorite ? 'fill-red-500' : ''}`} />
                                    {isFavorite ? 'Favori' : 'Ajouter aux favoris'}
                                </button>

                                {/* Share buttons */}
                                <div className="flex items-center gap-2 justify-center">
                                    <Share2 className="w-4 h-4 text-gray-400" />
                                    <a
                                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 rounded-full bg-gray-100 hover:bg-blue-600 text-gray-600 hover:text-white transition-colors"
                                    >
                                        <FacebookIcon />
                                    </a>
                                    <a
                                        href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${place.placeName} - ${window.location.href}`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 rounded-full bg-gray-100 hover:bg-green-500 text-gray-600 hover:text-white transition-colors"
                                    >
                                        <WhatsAppIcon />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Details - 3 columns layout */}
                    <div className="p-6 sm:p-8">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Column 1 - Contact Info */}
                            <div className="space-y-4">
                                {/* Address */}
                                {place.googleAddress && (
                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide">Adresse</p>
                                            <p className="text-gray-900">{place.googleAddress}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Phone */}
                                {place.phone && (
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide">Téléphone</p>
                                            <a href={`tel:${place.phone}`} className="text-gray-900 hover:text-cabaza-purple">
                                                {place.phone}
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {/* Email */}
                                {place.email && (
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide">Email</p>
                                            <a href={`mailto:${place.email}`} className="text-gray-900 hover:text-cabaza-purple">
                                                {place.email}
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {/* Website */}
                                {place.website && (
                                    <div className="flex items-center gap-3">
                                        <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide">Site web</p>
                                            <a
                                                href={place.website.startsWith('http') ? place.website : `https://${place.website}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-cabaza-purple hover:underline flex items-center gap-1"
                                            >
                                                {place.website.replace(/^https?:\/\//, '')}
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {/* Google Maps Link */}
                                {place.googleUrl && (
                                    <div className="flex items-center gap-3 print:hidden">
                                        <GoogleMapsIcon />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide">Google Maps</p>
                                            <button
                                                onClick={() => setShowGoogleMapsModal(true)}
                                                className="text-blue-600 hover:underline flex items-center gap-1"
                                            >
                                                Voir sur Google Maps
                                                <ExternalLink className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Column 2 - Additional Info */}
                            <div className="space-y-4">
                                {/* TVA */}
                                {place.tvaNum && (
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">N° TVA</p>
                                        <p className="text-gray-900">{place.tvaNum}</p>
                                    </div>
                                )}

                                {/* Firm Type */}
                                {place.firmType && (
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">Type d'entreprise</p>
                                        <p className="text-gray-900">{place.firmType}</p>
                                    </div>
                                )}

                                {/* Description */}
                                {place.description && (
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">Description</p>
                                        <p className="text-gray-700 text-sm">{place.description}</p>
                                    </div>
                                )}
                            </div>

                            {/* Column 3 - Map */}
                            {place.latitude && place.longitude && apiIsLoaded && (
                                <div className="print:hidden">
                                    <div className="rounded-lg overflow-hidden border border-gray-200 h-48 lg:h-full lg:min-h-[200px]">
                                        <Map
                                            defaultCenter={{
                                                lat: parseFloat(place.latitude),
                                                lng: parseFloat(place.longitude)
                                            }}
                                            defaultZoom={15}
                                            mapId="cabaza-map"
                                            disableDefaultUI={true}
                                            zoomControl={true}
                                        >
                                            <AdvancedMarker
                                                position={{
                                                    lat: parseFloat(place.latitude),
                                                    lng: parseFloat(place.longitude)
                                                }}
                                                title={place.placeName}
                                            />
                                        </Map>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Social Links */}
                        {(place.facebookUrl || place.twitterUrl || place.youtubeUrl) && (
                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Réseaux sociaux</p>
                                <div className="flex flex-wrap gap-3">
                                    {place.facebookUrl && (
                                        <a
                                            href={place.facebookUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
                                        >
                                            <FacebookIcon />
                                            Facebook
                                        </a>
                                    )}
                                    {place.youtubeUrl && (
                                        <a
                                            href={place.youtubeUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
                                        >
                                            YouTube
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Reviews Section */}
                        <div className="mt-8 pt-6 border-t border-gray-100 print:hidden">
                            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                <Star className="w-6 h-6 text-yellow-400" />
                                Avis ({stats.totalReviews})
                            </h2>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Left: Rating summary + Form */}
                                <div className="space-y-6">
                                    {/* Rating Summary */}
                                    {stats.totalReviews > 0 && (
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-center mb-4">
                                                <div className="text-4xl font-bold text-gray-900">{stats.averageRating.toFixed(1)}</div>
                                                <StarRating rating={Math.round(stats.averageRating)} size="lg" />
                                                <p className="text-sm text-gray-500 mt-1">{stats.totalReviews} avis</p>
                                            </div>
                                            <div className="space-y-2">
                                                {[5, 4, 3, 2, 1].map(stars => (
                                                    <RatingBar
                                                        key={stars}
                                                        stars={stars}
                                                        count={stats.ratingDistribution?.[stars] || 0}
                                                        total={stats.totalReviews}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Review Form */}
                                    {userId > 0 && !userReview && !editingReview && (
                                        <form id="review-form" onSubmit={handleSubmitReview} className="bg-purple-50 rounded-lg p-4">
                                            <h3 className="font-semibold text-gray-900 mb-3">Donner votre avis</h3>
                                            <div className="mb-4">
                                                <label className="block text-sm text-gray-600 mb-2">Votre note</label>
                                                <StarRating rating={newRating} onRate={setNewRating} interactive size="lg" />
                                            </div>
                                            <div className="mb-4">
                                                <label className="block text-sm text-gray-600 mb-2">Commentaire (optionnel)</label>
                                                <textarea
                                                    value={newComment}
                                                    onChange={(e) => setNewComment(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent resize-none"
                                                    rows={3}
                                                    placeholder="Partagez votre expérience..."
                                                    maxLength={1000}
                                                />
                                            </div>
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={newRating === 0 || submitting}
                                                loading={submitting}
                                                className="w-full"
                                            >
                                                <Send className="w-4 h-4 mr-2" />
                                                Publier
                                            </Button>
                                        </form>
                                    )}

                                    {/* Edit Form */}
                                    {editingReview && (
                                        <form id="review-form" onSubmit={handleSubmitReview} className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                            <h3 className="font-semibold text-gray-900 mb-3">Modifier votre avis</h3>
                                            <div className="mb-4">
                                                <label className="block text-sm text-gray-600 mb-2">Votre note</label>
                                                <StarRating rating={newRating} onRate={setNewRating} interactive size="lg" />
                                            </div>
                                            <div className="mb-4">
                                                <label className="block text-sm text-gray-600 mb-2">Commentaire</label>
                                                <textarea
                                                    value={newComment}
                                                    onChange={(e) => setNewComment(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent resize-none"
                                                    rows={3}
                                                    maxLength={1000}
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    onClick={handleCancelEdit}
                                                    className="flex-1"
                                                >
                                                    Annuler
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={newRating === 0 || submitting}
                                                    loading={submitting}
                                                    className="flex-1"
                                                >
                                                    Enregistrer
                                                </Button>
                                            </div>
                                        </form>
                                    )}
                                </div>

                                {/* Right: Reviews list */}
                                <div className="lg:col-span-2">
                                    {loadingReviews ? (
                                        <div className="text-center py-8">
                                            <div className="w-8 h-8 border-2 border-cabaza-purple border-t-transparent rounded-full animate-spin mx-auto"></div>
                                        </div>
                                    ) : reviews.length > 0 ? (
                                        <div className="space-y-4">
                                            {reviews.map(review => (
                                                <ReviewCard
                                                    key={review.id}
                                                    review={review}
                                                    isOwn={review.peopleId === userId}
                                                    onEdit={handleEditReview}
                                                    onDelete={handleDeleteReview}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 bg-gray-50 rounded-lg">
                                            <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500">Aucun avis pour le moment</p>
                                            <p className="text-sm text-gray-400 mt-1">Soyez le premier à donner votre avis !</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Google Maps Modal */}
            {showGoogleMapsModal && place?.googleUrl && (
                <div className="fixed inset-0 z-[10000] overflow-hidden">
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/50 transition-opacity"
                        onClick={() => setShowGoogleMapsModal(false)}
                    />

                    {/* Modal */}
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <GoogleMapsIcon />
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {place.placeName} - Google Maps
                                    </h3>
                                </div>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={place.googleUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                                    >
                                        Ouvrir dans Google Maps
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                    <button
                                        onClick={() => setShowGoogleMapsModal(false)}
                                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                    >
                                        <X className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                            </div>

                            {/* Content - iframe */}
                            <div className="flex-1 min-h-[500px]">
                                <iframe
                                    src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(place.googleAddress || place.placeName)}`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0, minHeight: '500px' }}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EndroitDetailContent;
